'use client';

import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import { IconArrowUpRight } from '../../../../components/ui/icons';
import { useController } from './controller';
import type { MediaCarouselProps } from './interfaces';

const MediaCarousel = <T,>({
    title,
    viewAllSrc,
    viewAllLabel,
    mediaList,
    titleClassName,
    autoplay,
    carouselContentClassName,
    renderItem,
    keyExtractor,
}: MediaCarouselProps<T>) => {
    const { carouselPlugins, getItemKey, renderItemContent } = useController<T>({
        autoplay,
        renderItem,
        keyExtractor,
    });

    return (
        <div>
            <div className="flex md:items-center md:justify-between mb-5 relative z-2">
                <h2 className={cn('font-bold text-xl md:text-2xl mx-5 mb-0 md:mb-0 md:ml-18', titleClassName)}>
                    <Link href={viewAllSrc}>
                        {typeof title === 'string' ? (
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: title comes from backend already stylised
                            <span dangerouslySetInnerHTML={{ __html: title }} />
                        ) : (
                            title
                        )}
                    </Link>
                </h2>
                <Link className="flex ml-auto md:ml-5 mr-2 md:mr-18 underline items-end" href={viewAllSrc}>
                    <span className="hidden md:inline">{viewAllLabel}</span>{' '}
                    <IconArrowUpRight className="ml-0 md:ml-2 text-[#DD06AE]" />
                </Link>
            </div>
            <div className="relative z-1">
                <Carousel
                    plugins={carouselPlugins}
                    opts={{
                        align: 'start',
                        slidesToScroll: 1,
                    }}
                    className="mt-[-28px] lg:mt-[-35px]"
                >
                    <CarouselContent className={cn('ml-2 md:ml-15 mr-5 py-7 lg:py-9', carouselContentClassName)}>
                        {mediaList
                            ? mediaList.map((item: T, index: number) => (
                                  <CarouselItem
                                      key={getItemKey(item, index)}
                                      className={'basis-[calc(100%/3.3)] md:basis-[calc(100%/6.3)]'}
                                  >
                                      {renderItemContent(item)}
                                  </CarouselItem>
                              ))
                            : null}
                    </CarouselContent>
                    <div className="hidden md:block absolute left-0 top-0 h-[100%] w-[95px]">
                        <CarouselPrevious
                            variant="ghost"
                            className="cursor-pointer absolute left-3 hover:bg-transparent disabled:opacity-0"
                        />
                    </div>
                    <div className="hidden md:block absolute right-0 top-0 h-[100%] w-[95px]">
                        <CarouselNext
                            variant="ghost"
                            className="cursor-pointer absolute right-3 hover:bg-transparent disabled:opacity-0"
                        />
                    </div>
                </Carousel>
            </div>
        </div>
    );
};

export default MediaCarousel;
